import { X, ChevronDown, RotateCcw, Printer, Mail } from 'lucide-react'
import { useModal } from './modal-container'
import { useTransaction, useEvent, useRefundTransaction, useReauth, useEmailTransactionReceipt, useGenerateLoginCode } from '@/lib/api/queries'
import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/date'
import { usePermission } from '@/lib/permissions'
import { useAuth } from '@/lib/auth'
import { printerService } from '@/lib/printer'
import { buildTransactionReceiptData, buildTransactionReceiptHtml } from '@/lib/printer/receipt-builders'
import { EmailReceiptModal } from '@/components/modals/email-receipt-modal'
import { PrinterSelectionModal } from '@/features/printer/components/PrinterSelectionModal'
import { getPrinterSettings } from '@/lib/printer/settings'
import { openReceiptPreview, printHtmlReceipt } from '@/lib/printer/system-print'
import { cardLabel, isCardSale } from '@/lib/payment-card'
import { FINIX_DASHBOARD_PAYMENT_URL, finixInstrumentUrl } from '@/lib/finix'
import { CardBrand } from '@/features/fraud/card-brand'

type TransactionTab = 'details' | 'order'

export function TransactionModal() {
  const { modalState, closeAllModals, openUserModal, openOrderModal } = useModal()
  const { user: authUser } = useAuth()
  const [activeTab, setActiveTab] = useState<TransactionTab>('details')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [showRefundForm, setShowRefundForm] = useState(false)
  const [refundAmount, setRefundAmount] = useState('')
  // Step-up re-auth: shown when the backend returns `reauth_required` because
  // the operator hasn't entered their password in the last 10 minutes.
  const [showReauth, setShowReauth] = useState(false)
  const [reauthPassword, setReauthPassword] = useState('')
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [showPrinterModal, setShowPrinterModal] = useState(false)
  const [printError, setPrintError] = useState<string | null>(null)
  const [printerState, setPrinterState] = useState(printerService.getState())

  const transactionId = modalState.transactionModal?.transactionId
  const eventId = modalState.transactionModal?.eventId

  const { data: transaction, isLoading, error } = useTransaction(transactionId || 0)
  const { data: event } = useEvent(eventId || 0)
  const refundMutation = useRefundTransaction()
  const reauthMutation = useReauth()
  const emailReceiptMutation = useEmailTransactionReceipt()
  const generateLoginCodeMutation = useGenerateLoginCode()
  const [generatedCode, setGeneratedCode] = useState<string | null>(null)
  
  // Permission checks
  const canViewOrderDetail = usePermission('can_view_order_detail')
  const canRefundAddcredit = usePermission('can_refund_addcredit')
  const canSeeUserInfo = usePermission('can_see_user_info')
  const canEditUser = usePermission('can_edit_users')

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAllModals()
    }
    if (modalState.transactionModal) {
      document.addEventListener('keydown', handleEscape)
      document.body.classList.add('overflow-hidden')
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.classList.remove('overflow-hidden')
    }
  }, [modalState.transactionModal, closeAllModals])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setDropdownOpen(false)
    if (dropdownOpen) {
      document.addEventListener('click', handleClickOutside)
    }
    return () => document.removeEventListener('click', handleClickOutside)
  }, [dropdownOpen])

  useEffect(() => {
    const unsubscribe = printerService.subscribe(setPrinterState)
    return unsubscribe
  }, [])

  // Reset generated code when transaction changes
  useEffect(() => {
    setGeneratedCode(null)
    generateLoginCodeMutation.reset()
  }, [transactionId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!modalState.transactionModal) return null

  const getTypeBadge = (action: string, amount: number) => {
    if (action === 'addCredit') {
      if (amount === 0) {
        return (
          <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-neutral-100 text-neutral-800 dark:bg-neutral-900/20 dark:text-neutral-400">
            Initialize
          </span>
        )
      }
      return (
        <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
          Reload
        </span>
      )
    }
    if (action === 'spendCredit') {
      return (
        <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">
          Order
        </span>
      )
    }
    if (action === 'transfer') {
      return (
        <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
          Transfer
        </span>
      )
    }
    if (action === 'refund') {
      return (
        <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
          Refund
        </span>
      )
    }
    if (action === 'voucherCredit') {
      return (
        <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">
          Voucher Credit
        </span>
      )
    }
    if (action === 'directPay') {
      return (
        <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-pink-100 text-pink-800 dark:bg-pink-900/20 dark:text-pink-400">
          {transaction && isCardSale(transaction) ? 'Card Sale' : 'Direct Pay'}
        </span>
      )
    }
    return (
      <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-neutral-100 text-neutral-800 dark:bg-neutral-900/20 dark:text-neutral-400">
        {action || 'Unknown'}
      </span>
    )
  }

  const handleUserClick = (userId: number) => {
    if (eventId) {
      closeAllModals()
      setTimeout(() => openUserModal(userId, eventId), 100)
    }
  }

  // Calculate pricing values
  const tokenValue = event?.token_value || 2 // Default to $2 per token
  const tokenAmount = transaction?.amount || 0
  const basePrice = tokenAmount * tokenValue
  
  // Get tax from fee_breakdown or tax_amount
  const getTax = () => {
    if (transaction?.fee_breakdown?.line_items) {
      const taxItem = transaction.fee_breakdown.line_items.find((item: { name: string; amount: number }) => item.name === 'Tax')
      return taxItem?.amount || 0
    }
    return transaction?.tax_amount || 0
  }
  
  const taxAmount = getTax()
  const feeAmount = transaction?.additional_fee_amount || 0
  
  // Calculate total
  const getTotalCharge = () => {
    if (transaction?.fee_breakdown?.customer_pays) {
      return transaction.fee_breakdown.customer_pays
    }
    if (transaction?.total_amount) {
      return transaction.total_amount
    }
    return basePrice + taxAmount + feeAmount
  }
  
  const totalCharge = getTotalCharge()

  const getDisplayAmount = () => {
    const amountValue = Math.round(transaction?.amount || 0)
    if (transaction?.action === 'refund') {
      return -Math.abs(amountValue)
    }
    if (transaction?.action === 'spendCredit' || transaction?.action === 'order') {
      return Math.abs(amountValue)
    }
    return amountValue
  }

  // Show refund option only for addCredit transactions with amount > 0 and not refunded
  const showRefundOption = transaction?.action === 'addCredit' && 
    (transaction?.amount || 0) > 0 && 
    !transaction?.refunded &&
    canRefundAddcredit

  const showActionsDropdown = !!transaction

  // Handle refund
  const handleRefund = () => {
    if (!transaction || !eventId) return

    const amount = parseFloat(refundAmount) || transaction.amount
    refundMutation.mutate(
      { transactionId: transaction.id, eventId, amount },
      {
        onSuccess: () => {
          setShowRefundForm(false)
          setRefundAmount('')
          setShowReauth(false)
          setReauthPassword('')
        },
        onError: (err) => {
          // Backend requires a fresh password (last auth > 10 min ago) —
          // surface the inline step-up prompt instead of a generic error.
          const code = (err as { data?: { code?: string } })?.data?.code
          if (code === 'reauth_required') {
            setShowReauth(true)
          }
        },
      }
    )
  }

  // Verify the password to open the step-up window, then retry the refund.
  const handleReauthSubmit = async () => {
    if (!reauthPassword) return
    try {
      await reauthMutation.mutateAsync(reauthPassword)
      setShowReauth(false)
      setReauthPassword('')
      handleRefund()
    } catch {
      // Wrong password / rate-limited — surfaced via reauthMutation state below.
    }
  }

  const handleGenerateLoginCode = async () => {
    if (!transaction || !eventId) return
    const user = transaction.to_user || transaction.user
    if (!user) return
    try {
      const result = await generateLoginCodeMutation.mutateAsync({ userId: user.id, eventId })
      setGeneratedCode(result.code)
    } catch {
      // Error is handled by mutation state
    }
  }

  const handlePrintReceipt = async () => {
    if (!transaction) return
    setPrintError(null)
    const settings = getPrinterSettings()
    if (settings.preferredConnection === 'system') {
      try {
        const html = buildTransactionReceiptHtml(transaction, event || undefined)
        if (settings.showPreviewBeforePrinting) {
          openReceiptPreview(html)
        } else {
          printHtmlReceipt(html)
        }
      } catch (error) {
        setPrintError(error instanceof Error ? error.message : 'Failed to open system print dialog')
      }
      return
    }

    const receiptData = buildTransactionReceiptData(transaction, event || undefined)
    const success = await printerService.printReceipt(receiptData)
    if (!success) {
      const printerError = printerService.getState().error || 'Failed to print receipt'
      setPrintError(printerError)
    }
  }

  const handleEmailReceipt = async () => {
    if (!transaction || !eventId) return
    await emailReceiptMutation.mutateAsync({ eventId, transactionId: transaction.id })
  }

  const defaultReceiptEmail = transaction?.to_user?.email
    || transaction?.user?.email
    || transaction?.from_user?.email
    || undefined

  const printerSettings = getPrinterSettings()

  return (
    <div className="fixed inset-0 z-110 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={closeAllModals}
      />

      {/* Modal content */}
      <div
        className="relative w-full max-w-4xl max-h-[80vh] bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 p-3 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 flex items-center">
            <svg className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="1" y1="10" x2="23" y2="10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Transaction #{transactionId}
          </h2>
          <button
            className="text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-200"
            onClick={closeAllModals}
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content area with sidebar layout */}
        <div className="flex flex-col md:flex-row max-h-[calc(80vh-52px)] overflow-hidden">
          {/* Sidebar navigation */}
          <div className="w-full md:w-64 border-b md:border-b-0 md:border-r border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950 shrink-0">
            <nav className="p-2 md:p-3">
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => setActiveTab('details')}
                    className={cn(
                      'w-full flex items-center p-3 text-left rounded-lg border-l-4 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500',
                      activeTab === 'details'
                        ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 border-blue-600 dark:border-blue-500'
                        : 'hover:bg-neutral-100 dark:hover:bg-neutral-900 text-neutral-800 dark:text-neutral-200 border-transparent'
                    )}
                  >
                    <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <line x1="1" y1="10" x2="23" y2="10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="font-semibold text-sm">Transaction Details</span>
                  </button>
                </li>
                {(transaction?.order || transaction?.action === 'directPay') && canViewOrderDetail && (
                  <li>
                    <button
                      onClick={() => setActiveTab('order')}
                      className={cn(
                        'w-full flex items-center p-3 text-left rounded-lg border-l-4 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500',
                        activeTab === 'order'
                          ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 border-blue-600 dark:border-blue-500'
                          : 'hover:bg-neutral-100 dark:hover:bg-neutral-900 text-neutral-800 dark:text-neutral-200 border-transparent'
                      )}
                    >
                      <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                        <line x1="3" y1="6" x2="21" y2="6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 10a4 4 0 0 1-8 0" />
                      </svg>
                      <span className="font-semibold text-sm">Order Breakdown</span>
                    </button>
                  </li>
                )}
              </ul>
            </nav>
          </div>

          {/* Main content area */}
          <div className="flex-1 overflow-y-auto p-3">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
              </div>
            ) : error ? (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-red-700 dark:text-red-400">Failed to load transaction: {error.message}</p>
              </div>
            ) : transaction ? (
              <>
                {/* Transaction Details Tab */}
                {activeTab === 'details' && (
                  <div className="space-y-4">
                    {printError && (
                      <div className="flex items-center justify-between gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                        <span>{printError}</span>
                        {printerSettings.preferredConnection !== 'system' && printerState.status !== 'connected' && (
                          <button
                            type="button"
                            onClick={() => setShowPrinterModal(true)}
                            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            Setup Printer
                          </button>
                        )}
                      </div>
                    )}
                    {/* Refund Status Badge */}
                    {!!transaction.refunded && (
                      <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 p-3 rounded-lg">
                        <div className="flex items-center">
                          <RotateCcw className="h-5 w-5 mr-2 text-orange-600 dark:text-orange-500" />
                          <span className="text-orange-800 dark:text-orange-200 font-medium">
                            {transaction.refunded === 2 
                              ? 'This transaction has been partially refunded'
                              : 'This transaction has been refunded'}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Transaction Overview Card */}
                    <div className="bg-white dark:bg-neutral-900 p-3 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="flex items-center text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                          <svg className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <line x1="1" y1="10" x2="23" y2="10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          Transaction Overview
                        </h3>
                        
                        {/* Actions Dropdown */}
                        {showActionsDropdown && (
                          <div className="relative">
                            <button 
                              onClick={(e) => {
                                e.stopPropagation()
                                setDropdownOpen(!dropdownOpen)
                              }}
                              className="inline-flex items-center px-2 py-1 border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-300 text-xs font-medium rounded-md transition-colors"
                              type="button"
                            >
                              <ChevronDown className={cn("h-3 w-3 transition-transform", dropdownOpen && "rotate-180")} />
                            </button>
                            
                            {/* Dropdown Menu */}
                            {dropdownOpen && (
                              <div className="absolute right-0 top-full mt-1 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-50">
                                <div className="py-1">
                                  <button 
                                    onClick={() => {
                                      setDropdownOpen(false)
                                      void handlePrintReceipt()
                                    }}
                                    className="w-full text-left px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-900 flex items-center whitespace-nowrap"
                                  >
                                    <Printer className="h-4 w-4 mr-2 shrink-0" />
                                    Print Receipt
                                  </button>
                                  <button 
                                    onClick={() => {
                                      setDropdownOpen(false)
                                      setShowEmailModal(true)
                                    }}
                                    className="w-full text-left px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-900 flex items-center whitespace-nowrap"
                                  >
                                    <Mail className="h-4 w-4 mr-2 shrink-0" />
                                    Email Receipt
                                  </button>
                                  {showRefundOption && (
                                    <button 
                                      onClick={() => {
                                        setDropdownOpen(false)
                                        setShowRefundForm(true)
                                      }}
                                      className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center whitespace-nowrap"
                                    >
                                      <RotateCcw className="h-4 w-4 mr-2 shrink-0" />
                                      Refund Transaction
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                          <div className="grid grid-cols-[80px_1fr] gap-2">
                            <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">ID:</span>
                            <span className="text-neutral-900 dark:text-neutral-100 font-mono text-sm font-bold">{transaction.id}</span>
                          </div>
                          <div className="grid grid-cols-[80px_1fr] gap-2">
                            <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Date:</span>
                            <span className="text-neutral-900 dark:text-neutral-100 text-sm">
                              {formatDate(transaction.timestamp, 'MMM d, yyyy h:mm a')}
                            </span>
                          </div>
                          <div className="grid grid-cols-[80px_1fr] gap-2">
                            <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Amount:</span>
                            <span className="text-neutral-900 dark:text-neutral-100 font-semibold text-sm">
                              {transaction.action === 'directPay' ? (
                                `$${(transaction.fee_breakdown?.total_amount ?? transaction.total_amount ?? 0).toFixed(2)}`
                              ) : (
                                `${getDisplayAmount()} Token${Math.abs(getDisplayAmount()) !== 1 ? 's' : ''}`
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="grid grid-cols-[80px_1fr] gap-2 items-center">
                            <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Type:</span>
                            <span>{getTypeBadge(transaction.action, transaction.amount || 0)}</span>
                          </div>
                          {/* Who performed a staff/standalone reload — from_user
                              is the operator, distinct from the credited user */}
                          {transaction.action === 'addCredit' && transaction.from_user &&
                            transaction.from_user.id !== transaction.user?.id && (
                            <div className="grid grid-cols-[80px_1fr] gap-2">
                              <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">By:</span>
                              <span className="text-neutral-900 dark:text-neutral-100 text-sm break-all">
                                {transaction.from_user.email
                                  || transaction.from_user.phone_number
                                  || `${transaction.from_user.first_name || ''} ${transaction.from_user.last_name || ''}`.trim()
                                  || `User #${transaction.from_user.id}`}
                              </span>
                            </div>
                          )}
                          {/* Order link if exists */}
                          {transaction.order && (
                            <div className="grid grid-cols-[80px_1fr] gap-2">
                              <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Order:</span>
                              <button 
                                onClick={() => setActiveTab('order')}
                                className="text-blue-600 dark:text-blue-500 hover:underline text-left text-sm font-semibold"
                              >
                                Order #{transaction.order.order_display_id || transaction.order.id}
                              </button>
                            </div>
                          )}
                          {/* Card info: the brand mark and last four when the
                              backend knows the card, linked to its Finix
                              instrument for staff; else the raw token. */}
                          {(transaction.payment_card || cardLabel(transaction.card)) && (
                            <div className="grid grid-cols-[80px_1fr] gap-2">
                              <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Card:</span>
                              {transaction.payment_card ? (() => {
                                const card = transaction.payment_card
                                const mark = <CardBrand brand={card.brand} last4={card.last4} wallet={card.wallet} />
                                const href = authUser?.is_superuser ? finixInstrumentUrl(card.instrument_id) : null
                                return href ? (
                                  <a
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Open in Finix dashboard"
                                    className="w-fit rounded text-neutral-900 hover:underline dark:text-neutral-100"
                                  >
                                    {mark}
                                  </a>
                                ) : (
                                  <span className="text-neutral-900 dark:text-neutral-100">{mark}</span>
                                )
                              })() : (
                                <span className="text-neutral-900 dark:text-neutral-100 font-mono text-xs">
                                  {cardLabel(transaction.card)}
                                </span>
                              )}
                            </div>
                          )}
                          {/* Charge ID */}
                          {transaction.stripe_charge_id && transaction.stripe_charge_id !== 'CASH' && (
                            <div className="grid grid-cols-[80px_1fr] gap-2">
                              <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Charge ID:</span>
                              {authUser?.is_superuser && transaction.stripe_charge_id.startsWith('TR') ? (
                                <a
                                  href={`${FINIX_DASHBOARD_PAYMENT_URL}/${transaction.stripe_charge_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Open in Finix dashboard"
                                  className="text-blue-600 dark:text-blue-400 hover:underline font-mono text-xs"
                                >
                                  {transaction.stripe_charge_id}
                                </a>
                              ) : (
                                <span className="text-neutral-900 dark:text-neutral-100 font-mono text-xs">{transaction.stripe_charge_id}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Pricing Breakdown Card (for directPay transactions) */}
                    {transaction.action === 'directPay' && transaction.fee_breakdown && (
                      <div className="bg-white dark:bg-neutral-900 p-3 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm">
                        <h3 className="flex items-center text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-3">
                          <svg className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2v20m9-9H3" />
                          </svg>
                          Pricing Breakdown
                        </h3>
                        <div className="space-y-3">
                          {/* Subtotal */}
                          <div className="flex justify-between items-center py-2 border-b border-neutral-200 dark:border-neutral-800">
                            <span className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">Subtotal:</span>
                            <span className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">
                              ${(transaction.fee_breakdown.base_amount ?? 0).toFixed(2)}
                            </span>
                          </div>

                          {/* Line items paid by user (tax, fees) */}
                          {transaction.fee_breakdown.line_items?.filter(li => li.paid_by === 'user').map((item, i) => (
                            <div key={i} className="flex justify-between items-center py-2 border-b border-neutral-200 dark:border-neutral-800">
                              <span className="text-neutral-700 dark:text-neutral-300 text-sm">{item.name}:</span>
                              <span className="text-neutral-900 dark:text-neutral-100 text-sm">
                                ${item.amount.toFixed(2)}
                              </span>
                            </div>
                          ))}

                          {/* Total */}
                          <div className="flex justify-between items-center py-2 pt-3">
                            <span className="font-bold text-lg text-neutral-900 dark:text-neutral-100 text-md">Total Charge:</span>
                            <span className="font-bold text-lg text-neutral-900 dark:text-neutral-100 text-md">
                              ${(transaction.fee_breakdown.total_amount ?? transaction.total_amount ?? 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Pricing Breakdown Card (for addCredit transactions) */}
                    {transaction.action === 'addCredit' && (transaction.amount || 0) > 0 && (
                      <div className="bg-white dark:bg-neutral-900 p-3 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm">
                        <h3 className="flex items-center text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-3">
                          <svg className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2v20m9-9H3" />
                          </svg>
                          Pricing Breakdown
                        </h3>
                        <div className="space-y-3">
                          {/* Token Amount and Base Cost */}
                          <div className="flex justify-between items-center py-2 border-b border-neutral-200 dark:border-neutral-800">
                            <span className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">
                              {Math.round(tokenAmount)} Token{Math.round(tokenAmount) !== 1 ? 's' : ''}:
                            </span>
                            <span className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">
                              ${basePrice.toFixed(2)}
                            </span>
                          </div>
                          
                          {/* Tax */}
                          <div className="flex justify-between items-center py-2 border-b border-neutral-200 dark:border-neutral-800">
                            <span className="text-neutral-700 dark:text-neutral-300 text-sm">Tax:</span>
                            <span className="text-neutral-900 dark:text-neutral-100 text-sm">
                              ${taxAmount.toFixed(2)}
                            </span>
                          </div>
                          
                          {/* Fee */}
                          <div className="flex justify-between items-center py-2 border-b border-neutral-200 dark:border-neutral-800">
                            <span className="text-neutral-700 dark:text-neutral-300 text-sm">Fee:</span>
                            <span className="text-neutral-900 dark:text-neutral-100 text-sm">
                              ${feeAmount.toFixed(2)}
                            </span>
                          </div>
                          
                          {/* Total Charge */}
                          <div className="flex justify-between items-center py-2 pt-3">
                            <span className="font-bold text-lg text-neutral-900 dark:text-neutral-100 text-md">Total Charge:</span>
                            <span className="font-bold text-lg text-neutral-900 dark:text-neutral-100 text-md">
                              ${totalCharge.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* User Information Card */}
                    <div className="bg-white dark:bg-neutral-900 p-3 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm">
                      <h3 className="flex items-center text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                        <svg className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" strokeWidth={2} />
                        </svg>
                        User Information
                      </h3>
                      <div className="space-y-3">
                        {/* Determine which user to show based on transaction type */}
                        {(() => {
                          // For addCredit/reload, show to_user or user
                          if (transaction.action === 'addCredit') {
                            const user = transaction.to_user || transaction.user
                            if (!user) return <span className="text-neutral-500 dark:text-neutral-400">System</span>
                            return (
                              <>
                                <div className="grid grid-cols-[80px_1fr] gap-2">
                                  <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Name:</span>
                                  {canSeeUserInfo ? (
                                    <button
                                      onClick={() => handleUserClick(user.id)}
                                      className="text-blue-600 dark:text-blue-500 hover:underline text-left text-sm font-semibold"
                                    >
                                      {user.first_name} {user.last_name}
                                    </button>
                                  ) : (
                                    <span className="text-neutral-900 dark:text-neutral-100 text-sm">{user.first_name}{user.last_name ? ` ${user.last_name.charAt(0)}.` : ''}</span>
                                  )}
                                </div>
                                {canSeeUserInfo && (
                                  <>
                                    <div className="grid grid-cols-[80px_1fr] gap-2">
                                      <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Email:</span>
                                      <span className="text-neutral-900 dark:text-neutral-100 text-sm">
                                        {user.email || '-'}
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-[80px_1fr] gap-2">
                                      <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Phone:</span>
                                      <span className="text-neutral-900 dark:text-neutral-100 text-sm">
                                        {user.phone_number || user.phone || '-'}
                                      </span>
                                    </div>
                                  </>
                                )}
                                {/* Generate Login Code - for Initialize transactions with inactive users */}
                                {(transaction.amount || 0) === 0 && user.is_active === false && canEditUser && (
                                  <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-800">
                                    {generatedCode ? (
                                      <div className="space-y-2">
                                        <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">Login Code:</span>
                                        <div className="flex items-center gap-3">
                                          <span className="font-mono text-2xl font-bold tracking-widest text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-4 py-2 rounded-lg border border-blue-200 dark:border-blue-800">
                                            {generatedCode}
                                          </span>
                                          <button
                                            onClick={() => {
                                              navigator.clipboard.writeText(generatedCode)
                                            }}
                                            className="px-3 py-2 text-xs font-medium text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-md transition-colors"
                                          >
                                            Copy
                                          </button>
                                        </div>
                                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                          This code expires in 10 minutes and is single-use.
                                        </p>
                                      </div>
                                    ) : (
                                      <div className="space-y-2">
                                        <button
                                          onClick={handleGenerateLoginCode}
                                          disabled={generateLoginCodeMutation.isPending}
                                          className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50"
                                        >
                                          <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                          </svg>
                                          {generateLoginCodeMutation.isPending ? 'Generating...' : 'Generate Login Code'}
                                        </button>
                                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                          Generate a one-time login code for this user who hasn't received their activation code.
                                        </p>
                                        {generateLoginCodeMutation.isError && (
                                          <p className="text-xs text-red-600 dark:text-red-400">
                                            {generateLoginCodeMutation.error?.message || 'Failed to generate login code'}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )
                          }

                          // For transfers and other types, show from/to
                          if (transaction.action === 'transfer' || transaction.action === 'spendCredit') {
                            return (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* From User */}
                                <div className="space-y-3">
                                  <h4 className="font-medium text-neutral-800 dark:text-neutral-200">From:</h4>
                                  {transaction.from_user || transaction.user ? (
                                    <>
                                      <div className="grid grid-cols-[80px_1fr] gap-2">
                                        <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Name:</span>
                                        {canSeeUserInfo ? (
                                          <button
                                            onClick={() => handleUserClick((transaction.from_user || transaction.user)!.id)}
                                            className="text-blue-600 dark:text-blue-500 hover:underline text-left text-sm"
                                          >
                                            {(transaction.from_user || transaction.user)!.first_name} {(transaction.from_user || transaction.user)!.last_name}
                                          </button>
                                        ) : (
                                          <span className="text-neutral-900 dark:text-neutral-100 text-sm">{(transaction.from_user || transaction.user)!.first_name}{(transaction.from_user || transaction.user)!.last_name ? ` ${(transaction.from_user || transaction.user)!.last_name.charAt(0)}.` : ''}</span>
                                        )}
                                      </div>
                                      {canSeeUserInfo && (
                                        <>
                                          <div className="grid grid-cols-[80px_1fr] gap-2">
                                            <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Email:</span>
                                            <span className="text-neutral-900 dark:text-neutral-100 text-sm">
                                              {(transaction.from_user || transaction.user)!.email || '-'}
                                            </span>
                                          </div>
                                          <div className="grid grid-cols-[80px_1fr] gap-2">
                                            <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Phone:</span>
                                            <span className="text-neutral-900 dark:text-neutral-100 text-sm">
                                              {(transaction.from_user || transaction.user)!.phone_number || (transaction.from_user || transaction.user)!.phone || '-'}
                                            </span>
                                          </div>
                                        </>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-neutral-500 dark:text-neutral-400">System</span>
                                  )}
                                </div>
                                
                                {/* To User */}
                                <div className="space-y-3">
                                  <h4 className="font-medium text-neutral-800 dark:text-neutral-200">To:</h4>
                                  {transaction.to_user ? (
                                    <>
                                      <div className="grid grid-cols-[80px_1fr] gap-2">
                                        <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Name:</span>
                                        {canSeeUserInfo ? (
                                          <button
                                            onClick={() => handleUserClick(transaction.to_user!.id)}
                                            className="text-blue-600 dark:text-blue-500 hover:underline text-left text-sm"
                                          >
                                            {transaction.to_user.first_name} {transaction.to_user.last_name}
                                          </button>
                                        ) : (
                                          <span className="text-neutral-900 dark:text-neutral-100 text-sm">{transaction.to_user.first_name}{transaction.to_user.last_name ? ` ${transaction.to_user.last_name.charAt(0)}.` : ''}</span>
                                        )}
                                      </div>
                                      {canSeeUserInfo && (
                                        <>
                                          <div className="grid grid-cols-[80px_1fr] gap-2">
                                            <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Email:</span>
                                            <span className="text-neutral-900 dark:text-neutral-100 text-sm">
                                              {transaction.to_user.email || '-'}
                                            </span>
                                          </div>
                                          <div className="grid grid-cols-[80px_1fr] gap-2">
                                            <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Phone:</span>
                                            <span className="text-neutral-900 dark:text-neutral-100 text-sm">
                                              {transaction.to_user.phone_number || transaction.to_user.phone || '-'}
                                            </span>
                                          </div>
                                        </>
                                      )}
                                    </>
                                  ) : transaction.order?.vendor_event?.vendor ? (
                                    <div className="grid grid-cols-[80px_1fr] gap-2">
                                      <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Vendor:</span>
                                      <span className="text-neutral-900 dark:text-neutral-100 text-sm">
                                        {transaction.order.vendor_event.vendor.name}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-neutral-500 dark:text-neutral-400">System</span>
                                  )}
                                </div>
                              </div>
                            )
                          }
                          
                          // Default: show user
                          const user = transaction.user
                          if (!user) return <span className="text-neutral-500 dark:text-neutral-400">System</span>
                          return (
                            <>
                              <div className="grid grid-cols-[80px_1fr] gap-2">
                                <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Name:</span>
                                {canSeeUserInfo ? (
                                  <button
                                    onClick={() => handleUserClick(user.id)}
                                    className="text-blue-600 dark:text-blue-500 hover:underline text-left text-sm font-semibold"
                                  >
                                    {user.first_name} {user.last_name}
                                  </button>
                                ) : (
                                  <span className="text-neutral-900 dark:text-neutral-100 text-sm">{user.first_name}{user.last_name ? ` ${user.last_name.charAt(0)}.` : ''}</span>
                                )}
                              </div>
                              {canSeeUserInfo && (
                                <>
                                  <div className="grid grid-cols-[80px_1fr] gap-2">
                                    <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Email:</span>
                                    <span className="text-neutral-900 dark:text-neutral-100 text-sm">
                                      {user.email || '-'}
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-[80px_1fr] gap-2">
                                    <span className="font-semibold text-neutral-700 dark:text-neutral-300 text-sm">Phone:</span>
                                    <span className="text-neutral-900 dark:text-neutral-100 text-sm">
                                      {user.phone_number || user.phone || '-'}
                                    </span>
                                  </div>
                                </>
                              )}
                            </>
                          )
                        })()}
                      </div>
                    </div>

                    {/* Refund Form */}
                    {showRefundForm && (
                      <div className="bg-red-50 dark:bg-red-900/20 p-4 border border-red-200 dark:border-red-800 rounded-lg">
                        <h3 className="flex items-center text-lg font-semibold text-red-800 dark:text-red-200 mb-3">
                          <RotateCcw className="h-5 w-5 mr-2" />
                          Refund Transaction
                        </h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-red-700 dark:text-red-300 mb-1">
                              Refund Amount (Tokens)
                            </label>
                            <input
                              type="number"
                              value={refundAmount}
                              onChange={(e) => setRefundAmount(e.target.value)}
                              placeholder={String(transaction.amount)}
                              max={transaction.amount}
                              min={1}
                              className="w-full px-3 py-2 border border-red-300 dark:border-red-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-red-500"
                            />
                            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                              Max: {transaction.amount} tokens
                            </p>
                          </div>
                          {showReauth && (
                            <div className="space-y-2 rounded-lg border border-red-300 dark:border-red-700 bg-white/60 dark:bg-neutral-900/40 p-3">
                              <label className="block text-sm font-medium text-red-700 dark:text-red-300">
                                Confirm your password to authorize this refund
                              </label>
                              <p className="text-xs text-red-600 dark:text-red-400">
                                Required because you haven&apos;t signed in within the last 10 minutes.
                              </p>
                              <input
                                type="password"
                                autoFocus
                                value={reauthPassword}
                                onChange={(e) => setReauthPassword(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleReauthSubmit() }}
                                placeholder="Password"
                                className="w-full px-3 py-2 border border-red-300 dark:border-red-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-red-500"
                              />
                              {reauthMutation.isError && (
                                <p className="text-sm text-red-600 dark:text-red-400">
                                  {(reauthMutation.error as { status?: number } | null)?.status === 429
                                    ? 'Too many attempts. Wait a few minutes and try again.'
                                    : 'Incorrect password. Try again.'}
                                </p>
                              )}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <button
                              onClick={showReauth ? handleReauthSubmit : handleRefund}
                              disabled={refundMutation.isPending || reauthMutation.isPending || (showReauth && !reauthPassword)}
                              className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                            >
                              {showReauth
                                ? (reauthMutation.isPending ? 'Verifying…' : 'Verify & Refund')
                                : (refundMutation.isPending ? 'Processing...' : 'Confirm Refund')}
                            </button>
                            <button
                              onClick={() => {
                                setShowRefundForm(false)
                                setRefundAmount('')
                                setShowReauth(false)
                                setReauthPassword('')
                              }}
                              className="px-4 py-2 bg-neutral-200 dark:bg-neutral-700 hover:bg-neutral-300 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-200 font-medium rounded-lg transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                          {refundMutation.isError && !showReauth && (
                            <p className="text-sm text-red-600 dark:text-red-400">
                              Error: {refundMutation.error?.message || 'Failed to process refund'}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Order Breakdown Tab — directPay without a linked order */}
                {activeTab === 'order' && !transaction.order && transaction.action === 'directPay' && canViewOrderDetail && (
                  <div className="space-y-4">
                    {/* Purchased Items */}
                    {transaction.purchased_items && transaction.purchased_items.length > 0 && (
                      <div className="bg-white dark:bg-neutral-900 p-3 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm">
                        <h3 className="flex items-center text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                          <svg className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                            <rect x="8" y="2" width="8" height="4" rx="1" ry="1" strokeWidth="2" />
                          </svg>
                          Purchased Items
                        </h3>
                        <div className="space-y-4">
                          {transaction.purchased_items.map((item) => {
                            const unitTokens = parseFloat(item.price_paid)
                            const unitDollars = unitTokens * tokenValue
                            const lineTotalDollars = unitDollars * item.quantity_total
                            return (
                              <div key={item.id} className="border-b border-neutral-200 dark:border-neutral-800 pb-3 last:border-b-0 last:pb-0">
                                <div className="flex items-start gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm text-neutral-900 dark:text-neutral-100">
                                      {item.redeemed_item_name}
                                    </div>
                                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-600 dark:text-neutral-400">
                                      <span>Qty {item.quantity_total}</span>
                                      <span>·</span>
                                      <span>${unitDollars.toFixed(2)} each</span>
                                      {item.is_redeemed ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                                          Redeemed{item.redeemed_at ? ` · ${formatDate(item.redeemed_at, 'MMM d, h:mm a')}` : ''}
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
                                          {item.quantity_remaining}/{item.quantity_total} remaining
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right shrink-0">
                                    <div className="font-semibold text-sm text-neutral-900 dark:text-neutral-100">${lineTotalDollars.toFixed(2)}</div>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {/* Purchase Summary */}
                    <div className="bg-white dark:bg-neutral-900 p-3 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm">
                      <h3 className="flex items-center text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                        <svg className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2v20m9-9H3" />
                        </svg>
                        Purchase Summary
                      </h3>
                      <div className="space-y-2">
                        {transaction.fee_breakdown && (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-neutral-700 dark:text-neutral-300">Subtotal</span>
                              <span className="text-neutral-900 dark:text-neutral-100">${(transaction.fee_breakdown.base_amount ?? 0).toFixed(2)}</span>
                            </div>
                            {transaction.fee_breakdown.line_items?.filter(li => li.paid_by === 'user').map((item, i) => (
                              <div key={i} className="flex justify-between text-sm">
                                <span className="text-neutral-700 dark:text-neutral-300">{item.name}</span>
                                <span className="text-neutral-900 dark:text-neutral-100">${item.amount.toFixed(2)}</span>
                              </div>
                            ))}
                            <div className="flex justify-between text-base font-semibold pt-2 border-t border-neutral-200 dark:border-neutral-800">
                              <span className="text-neutral-900 dark:text-neutral-100">Total</span>
                              <span className="text-neutral-900 dark:text-neutral-100">
                                ${(transaction.fee_breakdown.total_amount ?? transaction.total_amount ?? 0).toFixed(2)}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Order Breakdown Tab — transaction with a linked order */}
                {activeTab === 'order' && transaction.order && canViewOrderDetail && (
                  <div className="space-y-4">
                    {/* Order Summary Card */}
                    <div className="bg-white dark:bg-neutral-900 p-4 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm">
                      <h3 className="flex items-center text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                        <svg className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                          <line x1="3" y1="6" x2="21" y2="6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 10a4 4 0 0 1-8 0" />
                        </svg>
                        Order #{transaction.order.order_display_id || transaction.order.id}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                          <div className="grid grid-cols-[90px_1fr] gap-2">
                            <span className="font-semibold text-sm text-neutral-700 dark:text-neutral-300">Status:</span>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium w-fit
                              ${transaction.order.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-500' : ''}
                              ${transaction.order.status === 'cancelled' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-500' : ''}
                              ${transaction.order.status === 'refunded' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-500' : ''}
                              ${transaction.order.status === 'delivered' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-500' : ''}
                              ${!['completed', 'cancelled', 'refunded', 'delivered'].includes(transaction.order.status) ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-500' : ''}
                            `}>
                              {transaction.order.status?.charAt(0).toUpperCase() + transaction.order.status?.slice(1)}
                            </span>
                          </div>
                          <div className="grid grid-cols-[90px_1fr] gap-2">
                            <span className="font-semibold text-sm text-neutral-700 dark:text-neutral-300">Created:</span>
                            <span className="text-sm text-neutral-900 dark:text-neutral-100">
                              {transaction.order.created_at ? formatDate(transaction.order.created_at, 'MMM d, yyyy h:mm a') : '-'}
                            </span>
                          </div>
                          {transaction.order.completed_at && (
                            <div className="grid grid-cols-[90px_1fr] gap-2">
                              <span className="font-semibold text-sm text-neutral-700 dark:text-neutral-300">Completed:</span>
                              <span className="text-sm text-neutral-900 dark:text-neutral-100">
                                {formatDate(transaction.order.completed_at, 'MMM d, yyyy h:mm a')}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="space-y-3">
                          <div className="grid grid-cols-[80px_1fr] gap-2">
                            <span className="font-semibold text-sm text-neutral-700 dark:text-neutral-300">Total:</span>
                            <span className="text-sm text-neutral-900 dark:text-neutral-100">
                              {transaction.order.order_total || transaction.order.total || getDisplayAmount()} Tokens
                            </span>
                          </div>
                          {transaction.order.vendor_event?.vendor && (
                            <div className="grid grid-cols-[80px_1fr] gap-2">
                              <span className="font-semibold text-sm text-neutral-700 dark:text-neutral-300">Vendor:</span>
                              <span className="text-sm text-neutral-900 dark:text-neutral-100">
                                {transaction.order.vendor_event.vendor.name}
                              </span>
                            </div>
                          )}
                          {transaction.order.vendor_event?.booth && (
                            <div className="grid grid-cols-[80px_1fr] gap-2">
                              <span className="font-semibold text-sm text-neutral-700 dark:text-neutral-300">Location:</span>
                              <span className="text-sm text-neutral-900 dark:text-neutral-100">
                                {transaction.order.vendor_event.booth}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Order Items */}
                    {transaction.order.order_items && transaction.order.order_items.length > 0 && (
                      <div className="bg-white dark:bg-neutral-900 p-3 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm">
                        <h3 className="flex items-center text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                          <svg className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                            <rect x="8" y="2" width="8" height="4" rx="1" ry="1" strokeWidth="2" />
                          </svg>
                          Order Items
                        </h3>
                        <div className="space-y-4">
                          {transaction.order.order_items.map((item, index) => {
                            const vendorItem = typeof item.vendor_event_item === 'object' ? item.vendor_event_item : null
                            return (
                              <div key={index} className="border-b border-neutral-200 dark:border-neutral-800 pb-3 last:border-b-0 last:pb-0">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div className="space-y-1">
                                    <div className="font-medium text-sm text-neutral-900 dark:text-neutral-100">
                                      {vendorItem?.name || item.name || 'Unknown Item'}
                                    </div>
                                    {vendorItem?.description && (
                                      <div className="text-xs text-neutral-600 dark:text-neutral-400">
                                        {vendorItem.description}
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-center">
                                    <div className="text-xs text-neutral-600 dark:text-neutral-400">Quantity</div>
                                    <div className="font-medium text-sm text-neutral-900 dark:text-neutral-100">{item.quantity || item.qty || 1}</div>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-xs text-neutral-600 dark:text-neutral-400">Total</div>
                                    <div className="font-medium text-sm text-neutral-900 dark:text-neutral-100">{item.total || 0} Tokens</div>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {/* View Full Order Details Button */}
                    <div className="text-center">
                      <button
                        onClick={() => {
                          if (transaction.order) {
                            closeAllModals()
                            setTimeout(() => openOrderModal(transaction.order!.id, eventId!), 100)
                          }
                        }}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors text-sm"
                      >
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" strokeWidth="2" />
                        </svg>
                        View Full Order Details
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>

    <EmailReceiptModal
      isOpen={showEmailModal}
      title="Email Transaction Receipt"
      defaultEmail={defaultReceiptEmail}
      onClose={() => setShowEmailModal(false)}
      onSend={handleEmailReceipt}
    />
    <PrinterSelectionModal
      isOpen={showPrinterModal}
      onClose={() => setShowPrinterModal(false)}
    />
    </div>
  )
}
